import { useEffect, useRef } from 'react';

const FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

export const focusableIn = (root) => (root ? Array.from(root.querySelectorAll(FOCUSABLE)) : []);

// Make every <body> child except `layer` inert; returns the undo.
// Children that were inert already are left alone.
const inertAllBut = (layer) => {
    if (!layer || !document.body) return () => {};
    const changed = [...document.body.children].filter(
        (el) => el !== layer && !el.contains(layer) && !el.hasAttribute('inert'),
    );
    changed.forEach((el) => el.setAttribute('inert', ''));
    return () => changed.forEach((el) => el.removeAttribute('inert'));
};

/**
 * Modal focus handling for Drawer and Dialog: while `active`, focus moves
 * into `containerRef` (to `initialFocusRef` or the first focusable control),
 * Tab and Shift+Tab cycle inside it, Escape calls `onEscape`. With
 * `layerRef` (the layer's root, a direct child of <body>) everything else in
 * <body> — the app and its sticky top bar — is `inert` while it is open.
 * On close the background is live again and focus returns to whatever had
 * it before, unless the caller has already moved focus somewhere on purpose
 * (e.g. back to a table row).
 *
 * Returns an onKeyDown handler for the container.
 */
export default function useModalFocus(containerRef, active, { onEscape, initialFocusRef, layerRef } = {}) {
    const onEscapeRef = useRef(onEscape);
    onEscapeRef.current = onEscape;

    useEffect(() => {
        if (!active) return undefined;
        const previous = document.activeElement;
        const container = containerRef.current;
        const undoInert = layerRef ? inertAllBut(layerRef.current) : () => {};
        const target = initialFocusRef?.current || focusableIn(container)[0] || container;
        target?.focus();
        return () => {
            undoInert();
            const now = document.activeElement;
            const focusLost = !now || now === document.body || (container && container.contains(now));
            if (focusLost && previous && previous !== document.body
                && typeof previous.focus === 'function' && document.contains(previous)) {
                previous.focus();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    return (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            onEscapeRef.current?.();
            return;
        }
        if (e.key !== 'Tab') return;
        const items = focusableIn(containerRef.current);
        if (!items.length) {
            e.preventDefault();
            return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };
}
